import {
  FormEvent,
  KeyboardEvent as ReactKeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import debounce from "lodash/debounce";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { cn } from "~/lib/utils";
import {
  autoSuggestWhat3Words,
  convertWhat3WordsToCoordinates,
  What3WordsSuggestion,
} from "~/modules/location/what3words.client";

type LocationEntryMode = "gps" | "what3words";

export interface EditLocationDialogSubmitInput {
  latitude: number;
  longitude: number;
}

export interface EditLocationModalProps {
  isOpen: boolean;
  defaultLongitude: string;
  defaultLatitude: string;
  defaultWhat3Words?: string;
  onCancel: () => void;
  onConfirm: (input: EditLocationDialogSubmitInput) => Promise<void> | void;
  error?: string;
  loading?: boolean;
}

export const EditLocationDialog = ({
  isOpen,
  onCancel,
  onConfirm,
  error,
  defaultLongitude,
  defaultLatitude,
  defaultWhat3Words,
  loading = false,
}: EditLocationModalProps) => {
  const [longitude, setLongitude] = useState(defaultLongitude);
  const [latitude, setLatitude] = useState(defaultLatitude);
  const [entryMode, setEntryMode] = useState<LocationEntryMode>("gps");
  const [what3WordsInput, setWhat3WordsInput] = useState(
    defaultWhat3Words ?? ""
  );
  const [what3WordsSuggestions, setWhat3WordsSuggestions] = useState<
    What3WordsSuggestion[]
  >([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [hasSearchedSuggestions, setHasSearchedSuggestions] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const [isResolvingWhat3Words, setIsResolvingWhat3Words] = useState(false);
  const [highlightedSuggestionIndex, setHighlightedSuggestionIndex] =
    useState<number>(-1);
  const suggestionsRequestIdRef = useRef(0);

  const resetSuggestions = () => {
    suggestionsRequestIdRef.current += 1;
    setWhat3WordsSuggestions([]);
    setIsLoadingSuggestions(false);
    setHasSearchedSuggestions(false);
    setHighlightedSuggestionIndex(-1);
  };

  const debouncedSuggestWhat3Words = useMemo(
    () =>
      debounce(async (value: string) => {
        const requestId = suggestionsRequestIdRef.current + 1;
        suggestionsRequestIdRef.current = requestId;
        setIsLoadingSuggestions(true);
        setHasSearchedSuggestions(true);

        try {
          const suggestions = await autoSuggestWhat3Words(value);

          if (suggestionsRequestIdRef.current !== requestId) {
            return;
          }

          setWhat3WordsSuggestions(suggestions);
          setHighlightedSuggestionIndex(suggestions.length > 0 ? 0 : -1);
        } catch (suggestionError) {
          if (suggestionsRequestIdRef.current !== requestId) {
            return;
          }

          console.error(
            "Unable to load What3Words suggestions",
            suggestionError
          );
          setWhat3WordsSuggestions([]);
          setLocalError(
            suggestionError instanceof Error
              ? suggestionError.message
              : "Unable to load What3Words suggestions."
          );
        } finally {
          if (suggestionsRequestIdRef.current === requestId) {
            setIsLoadingSuggestions(false);
          }
        }
      }, 300),
    []
  );

  useEffect(
    () => () => debouncedSuggestWhat3Words.cancel(),
    [debouncedSuggestWhat3Words]
  );

  useEffect(() => {
    if (isOpen) {
      setLongitude(defaultLongitude);
      setLatitude(defaultLatitude);
      setWhat3WordsInput(defaultWhat3Words ?? "");
      setEntryMode(defaultWhat3Words ? "what3words" : "gps");
      setLocalError(null);
      debouncedSuggestWhat3Words.cancel();
      resetSuggestions();
    }
  }, [
    debouncedSuggestWhat3Words,
    defaultLatitude,
    defaultLongitude,
    defaultWhat3Words,
    isOpen,
  ]);

  const handleEntryModeChange = (value: string) => {
    setEntryMode(value as LocationEntryMode);
    setLocalError(null);
    debouncedSuggestWhat3Words.cancel();
    resetSuggestions();
  };

  const handleWhat3WordsChange = (value: string) => {
    setWhat3WordsInput(value);
    setLocalError(null);
    setHighlightedSuggestionIndex(-1);
    debouncedSuggestWhat3Words.cancel();
    resetSuggestions();

    const trimmedValue = value.trim();
    if (
      !trimmedValue ||
      trimmedValue.length < 3 ||
      (trimmedValue.match(/\./g) || []).length < 2
    ) {
      return;
    }

    debouncedSuggestWhat3Words(trimmedValue);
  };

  const handleSuggestionSelect = (suggestion: What3WordsSuggestion) => {
    setWhat3WordsInput(suggestion.words);
    setLocalError(null);
    debouncedSuggestWhat3Words.cancel();
    resetSuggestions();
  };

  const handleWhat3WordsKeyDown = (
    event: ReactKeyboardEvent<HTMLInputElement>
  ) => {
    if (!showSuggestions || what3WordsSuggestions.length === 0) {
      return;
    }

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedSuggestionIndex((currentIndex) =>
        currentIndex >= what3WordsSuggestions.length - 1 ? 0 : currentIndex + 1
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedSuggestionIndex((currentIndex) =>
        currentIndex <= 0 ? what3WordsSuggestions.length - 1 : currentIndex - 1
      );
      return;
    }

    if (event.key === "Enter" && highlightedSuggestionIndex >= 0) {
      event.preventDefault();
      handleSuggestionSelect(what3WordsSuggestions[highlightedSuggestionIndex]);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLocalError(null);

    if (entryMode === "gps") {
      const latitudeFloat = parseFloat(latitude);
      const longitudeFloat = parseFloat(longitude);

      if (Number.isNaN(latitudeFloat) || Number.isNaN(longitudeFloat)) {
        setLocalError("Invalid latitude or longitude");
        return;
      }

      try {
        await onConfirm({
          latitude: latitudeFloat,
          longitude: longitudeFloat,
        });
      } catch (submitError) {
        console.error("Unable to save GPS location", submitError);
        setLocalError(
          submitError instanceof Error
            ? submitError.message
            : String(submitError)
        );
      }
      return;
    }

    setIsResolvingWhat3Words(true);
    try {
      const coordinates = await convertWhat3WordsToCoordinates(what3WordsInput);
      await onConfirm(coordinates);
      resetSuggestions();
    } catch (submitError) {
      console.error("Unable to resolve What3Words location", submitError);
      setLocalError(
        submitError instanceof Error
          ? submitError.message
          : "Unable to resolve What3Words location."
      );
    } finally {
      setIsResolvingWhat3Words(false);
    }
  };

  const showSuggestions =
    entryMode === "what3words" &&
    what3WordsInput.trim().length >= 3 &&
    (what3WordsInput.trim().match(/\./g) || []).length >= 2 &&
    (isLoadingSuggestions ||
      hasSearchedSuggestions ||
      what3WordsSuggestions.length > 0);
  const displayedError = localError || error;
  const isSaving = loading || isResolvingWhat3Words;
  const highlightedSuggestionId =
    highlightedSuggestionIndex >= 0
      ? `pes-test-what3words-suggestion-${highlightedSuggestionIndex}`
      : undefined;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogContent>
        <form className="space-y-6" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Edit Location</DialogTitle>
            <DialogDescription>
              Edit the location of this test by providing either GPS coordinates
              or a What3Words address.
            </DialogDescription>
          </DialogHeader>

          <Tabs value={entryMode} onValueChange={handleEntryModeChange}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="gps">GPS</TabsTrigger>
              <TabsTrigger value="what3words">What3Words</TabsTrigger>
            </TabsList>

            <TabsContent value="gps" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pes-test-latitude">Latitude</Label>
                <Input
                  id="pes-test-latitude"
                  value={latitude}
                  onChange={(event) => {
                    setLatitude(event.target.value);
                    setLocalError(null);
                  }}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="pes-test-longitude">Longitude</Label>
                <Input
                  id="pes-test-longitude"
                  value={longitude}
                  onChange={(event) => {
                    setLongitude(event.target.value);
                    setLocalError(null);
                  }}
                />
              </div>
            </TabsContent>

            <TabsContent value="what3words" className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="pes-test-what3words">What3Words address</Label>
                <Input
                  id="pes-test-what3words"
                  value={what3WordsInput}
                  onChange={(event) =>
                    handleWhat3WordsChange(event.target.value)
                  }
                  onKeyDown={handleWhat3WordsKeyDown}
                  placeholder="filled.count.soap"
                  autoComplete="off"
                  spellCheck={false}
                  role="combobox"
                  aria-expanded={showSuggestions}
                  aria-autocomplete="list"
                  aria-controls="pes-test-what3words-suggestions"
                  aria-activedescendant={highlightedSuggestionId}
                />
                <p className="text-sm text-muted-foreground">
                  Start typing a full what3words address to see suggestions.
                </p>
              </div>

              {showSuggestions && (
                <div
                  id="pes-test-what3words-suggestions"
                  role="listbox"
                  className="overflow-hidden rounded-md border"
                >
                  {isLoadingSuggestions ? (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      Loading suggestions...
                    </p>
                  ) : what3WordsSuggestions.length > 0 ? (
                    <div className="divide-y">
                      {what3WordsSuggestions.map((suggestion, index) => (
                        <button
                          key={suggestion.words}
                          id={`pes-test-what3words-suggestion-${index}`}
                          type="button"
                          role="option"
                          aria-selected={index === highlightedSuggestionIndex}
                          className={cn(
                            "hover:bg-accent hover:text-accent-foreground flex w-full flex-col items-start gap-1 px-3 py-2 text-left transition-colors",
                            index === highlightedSuggestionIndex &&
                              "bg-accent text-accent-foreground"
                          )}
                          onMouseEnter={() =>
                            setHighlightedSuggestionIndex(index)
                          }
                          onClick={() => handleSuggestionSelect(suggestion)}
                        >
                          <span className="text-sm font-medium">
                            {suggestion.words}
                          </span>
                          {(suggestion.nearestPlace ||
                            suggestion.country ||
                            typeof suggestion.distance === "number") && (
                            <span className="text-xs text-muted-foreground">
                              {[
                                suggestion.nearestPlace,
                                suggestion.country,
                                typeof suggestion.distance === "number"
                                  ? `${Math.round(suggestion.distance)} km`
                                  : null,
                              ]
                                .filter(Boolean)
                                .join(" • ")}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <p className="px-3 py-2 text-sm text-muted-foreground">
                      No suggestions found.
                    </p>
                  )}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <div className="space-y-2">
            <p className="text-sm text-muted-foreground">
              Changing the location will trigger reprocessing of the test, which
              can take a few minutes.
            </p>
            {displayedError && (
              <p className="text-sm text-destructive">{displayedError}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
